import { languageSchema } from "./languageSchema";

export const getTicketsSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      page: { type: "number" },
      limit: { type: "number" },
      checkedIn: { type: "boolean" },
      tagMatch: { type: "string", enum: ["all", "any"] },
      searchByName: { type: "boolean" },
      ...languageSchema.properties,
    },
    required: ["eventId"],
  },
};

export const getTicketByIdSchema = {
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
      eventId: { type: "number" },
    },
  },
};

export const getTicketBySecretCodeSchema = {
  params: {
    type: "object",
    properties: {
      secretCode: { type: "string" },
    },
    required: ["secretCode"],
  },
};

export const createTicketSchema = {
  body: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      userData: {
        type: "object",
        properties: {
          firstName: { type: "string", minLength: 1 },
          lastName: { type: "string", minLength: 1 },
          email: { type: ["string", "null"], format: "email" },
          companyName: { type: ["string", "null"] },
          categoryOfUser: { type: "array", items: { type: "number" } },
        },
        required: ["firstName", "lastName"],
      },
    },
    required: ["eventId", "userData"],
  },
};

export const patchTicketSchema = {
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
  },
  body: {
    type: "object",
    properties: {
      checkedInAt: { type: ["string", "null"], format: "date-time" },
      checkInMethod: { type: "string", enum: ["scan", "hand"] },
      categoryOfUser: { type: "array", items: { type: "number" } },
      customFields: { type: "object" },
      firstName: { type: "string", minLength: 1 },
      lastName: { type: "string", minLength: 1 },
      companyName: { type: ["string", "null"] },
      email: { type: "string", format: "email" },
    },
    additionalProperties: false,
  },
};

const ticketsImportFileSchema = {
  type: "object",
  properties: {
    data: { type: "string" },
    name: { type: "string" },
    type: {
      type: "string",
      enum: [
        "text/csv",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ],
    },
  },
  required: ["data", "name", "type"],
};

export const createTicketsImportSchema = {
  body: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      file: ticketsImportFileSchema,
      tagResolution: { type: "string", enum: ["skip", "create"] },
    },
    required: ["eventId", "file"],
  },
};

export const validateTicketsImportSchema = {
  body: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      file: ticketsImportFileSchema,
    },
    required: ["eventId", "file"],
  },
};

export const getUserTicketsByEventSchema = {
  params: {
    type: "object",
    properties: {
      userId: { type: "number" },
      eventId: { type: "number" },
    },
    required: ["userId", "eventId"],
  },
};

export const getTicketHistorySchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
      page: { type: "number", minimum: 1 },
      limit: { type: "number", minimum: 1, maximum: 100 },
      types: { type: "string" },
      search: { type: "string" },
    },
    required: ["eventId"],
  },
};

export const getTicketHistoryByIdSchema = {
  params: {
    type: "object",
    properties: {
      id: { type: "number" },
    },
    required: ["id"],
  },
};

export const getTicketRegisterSummarySchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
};

export const deleteTicketSchema = {
  body: {
    type: "object",
    properties: { id: { type: "number" } },
    required: ["id"],
  },
};

export const getTicketColumnsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
  },
  required: ["eventId"],
};

export const patchTicketColumnsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
  },
  body: {
    type: "object",
    properties: {
      columns: {
        type: "array",
        items: {
          type: "object",
          properties: {
            name: { type: "string" },
            isVisible: { type: "boolean" },
          },
          required: ["name", "isVisible"],
        },
      },
    },
    required: ["columns"],
  },
};

export const getTicketCustomColumnsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
  },
  querystring: {
    type: "object",
    properties: {
      ticketId: { type: "number" },
    },
  },
  required: ["eventId"],
};

export const patchTicketCustomColumnsSchema = {
  params: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
  body: {
    type: "object",
    properties: {
      ticketId: { type: "number" },
      data: {
        type: "object",
      },
    },
    required: ["ticketId", "data"],
  },
};

export const getExportTicketsSchema = {
  querystring: {
    type: "object",
    properties: {
      eventId: { type: "number" },
    },
    required: ["eventId"],
  },
};

// The Create Ticket form as the resolver sees it: empty fields arrive as null
// (the sanitizer turns "" into null), and the category is the one picked tag
// id. The form sends it as `categoryOfUser: [id]`; with none, the API gives
// the ticket the Attendee category.
export const web_createTicketSchema = {
  type: "object",
  properties: {
    firstName: { type: "string", minLength: 1 },
    lastName: { type: "string", minLength: 1 },
    email: {
      anyOf: [
        { type: "null" },
        { type: "string", maxLength: 0 },
        { type: "string", format: "email" },
      ],
      errorMessage: "Invalid email format.",
    },
    companyName: { type: ["string", "null"] },
    categoryOfUser: { type: ["string", "null"] },
  },
  required: ["firstName", "lastName"],
};

export const web_checkInAttendeeSchema = {
  type: "object",
  properties: {
    firstName: { type: "string" },
    lastName: { type: "string" },
    email: web_createTicketSchema.properties.email,
    categoryOfUser: { type: "array", items: { type: "string" }, minItems: 1 },
    customFields: {
      type: "object",
      additionalProperties: { type: ["string", "null"] },
    },
  },
  required: ["firstName", "lastName", "categoryOfUser"],
};

// Claim codes are 8 characters from ABCDEFGHJKLMNPQRSTUVWXYZ23456789. The API
// also accepts lower case, spaces and a dash ("abcd-2345"), so the schema only
// bounds the length and the service normalizes and checks the code.
const claimCodeInputSchema = { type: "string", minLength: 1, maxLength: 32 };

export const getTicketClaimPreviewSchema = {
  params: {
    type: "object",
    properties: {
      claimCode: claimCodeInputSchema,
    },
    required: ["claimCode"],
  },
};

export const claimTicketSchema = {
  body: {
    type: "object",
    properties: {
      claimCode: claimCodeInputSchema,
      // "link" when the code came from a claim link rather than typed in.
      via: { type: "string", enum: ["code", "link"], default: "code" },
    },
    required: ["claimCode"],
    additionalProperties: false,
  },
};

export const rotateTicketClaimCodeSchema = {
  params: {
    type: "object",
    properties: {
      ticketId: { type: "number" },
    },
    required: ["ticketId"],
  },
};

// Take a ticket out of the app. It keeps its code unless newCode is set.
export const unclaimTicketSchema = {
  ...rotateTicketClaimCodeSchema,
  body: {
    type: ["object", "null"],
    properties: {
      newCode: { type: "boolean" },
    },
    additionalProperties: false,
  },
};

// "Email the ticket": the default ticket email to one ticket.
export const sendTicketEmailSchema = rotateTicketClaimCodeSchema;

export const TICKET_CLAIM_STATUSES = ["available", "claimed", "yours"];
export const TICKET_CLAIMED_VIA = [
  "email",
  "link",
  "code",
  "registration",
  "organizer",
  "migration",
];
